import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSpellCheck } from "../../hooks/useSpellCheck.js";

const COPY_STYLE_PROPS = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "fontVariant",
  "fontStretch",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "textTransform",
  "textIndent",
  "textAlign",
  "tabSize",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderTopStyle",
  "borderRightStyle",
  "borderBottomStyle",
  "borderLeftStyle",
  "boxSizing",
];

const LONG_PRESS_MS = 500;
const LONG_PRESS_TOLERANCE_PX = 8;

function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function buildOverlayHtml(text, issues) {
  if (!text) return "";
  if (!issues?.length) return escapeHtml(text);
  const sorted = [...issues].sort((a, b) => a.pos - b.pos);
  let cursor = 0;
  let html = "";
  for (let i = 0; i < sorted.length; i++) {
    const it = sorted[i];
    if (it.pos < cursor) continue;
    if (it.pos > cursor) html += escapeHtml(text.slice(cursor, it.pos));
    const word = text.substr(it.pos, it.len);
    html += `<span class="spellErr" data-issue="${i}">${escapeHtml(word)}</span>`;
    cursor = it.pos + it.len;
  }
  if (cursor < text.length) html += escapeHtml(text.slice(cursor));
  // Завершающий перенос строки нужен, чтобы пустая последняя строка
  // не «съедалась» при подсчёте высоты в зеркале.
  if (text.endsWith("\n")) html += "\u200b";
  return html;
}

function findIssueAtOffset(issues, offset) {
  if (!issues?.length || offset == null || offset < 0) return null;
  for (const it of issues) {
    if (offset >= it.pos && offset <= it.pos + it.len) return it;
  }
  return null;
}

function getCaretOffsetFromPoint(el, clientX, clientY) {
  // Стандартный путь (Firefox)
  if (typeof document.caretPositionFromPoint === "function") {
    const cp = document.caretPositionFromPoint(clientX, clientY);
    if (cp && cp.offsetNode) return cp.offset;
  }
  // WebKit/Blink
  if (typeof document.caretRangeFromPoint === "function") {
    const range = document.caretRangeFromPoint(clientX, clientY);
    if (range) return range.startOffset;
  }
  return null;
}

export const SpellCheckedTextarea = forwardRef(function SpellCheckedTextarea(
  {
    value,
    onChange,
    onScroll,
    spellCheckEnabled = true,
    spellLang = "ru,en",
    className = "",
    overlayClassName = "",
    style,
    ...rest
  },
  ref,
) {
  const taRef = useRef(null);
  const overlayRef = useRef(null);
  const wrapRef = useRef(null);
  useImperativeHandle(ref, () => taRef.current, []);

  const issues = useSpellCheck(value || "", { lang: spellLang, enabled: spellCheckEnabled });

  const [menu, setMenu] = useState(null);

  const closeMenu = useCallback(() => setMenu(null), []);

  /** Координаты и высота меню: портал в body + учёт visualViewport (Telegram WebView). */
  const placeMenu = useCallback((clientX, clientY, suggestionCount) => {
    const pad = 10;
    const rowH = 40;
    const titleBlock = 34;
    const contentH = titleBlock + Math.max(1, suggestionCount) * rowH + 12;
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    const vTop = vv?.offsetTop ?? 0;
    const vLeft = vv?.offsetLeft ?? 0;
    const vH = vv?.height ?? window.innerHeight;
    const vW = vv?.width ?? window.innerWidth;
    const menuW = Math.min(260, vW - pad * 2);
    const maxH = Math.min(280, Math.floor(vH * 0.5), contentH);
    let x = clientX - menuW * 0.33;
    x = Math.max(vLeft + pad, Math.min(x, vLeft + vW - menuW - pad));
    const spaceBelow = vTop + vH - clientY - pad;
    const spaceAbove = clientY - vTop - pad;
    let y = clientY + 6;
    if (spaceBelow < maxH && spaceAbove > spaceBelow) {
      y = clientY - maxH - 6;
    }
    y = Math.max(vTop + pad, Math.min(y, vTop + vH - maxH - pad));
    return { x, y, maxHeight: maxH };
  }, []);

  // Синхронизируем стили textarea -> overlay (на случай тем/смены шрифта).
  useLayoutEffect(() => {
    const ta = taRef.current;
    const ov = overlayRef.current;
    if (!ta || !ov) return;
    const cs = window.getComputedStyle(ta);
    for (const prop of COPY_STYLE_PROPS) {
      ov.style[prop] = cs[prop];
    }
    // Цвет рамки оверлея делаем прозрачным — нам нужны её размеры,
    // а саму рамку рисует textarea сверху.
    ov.style.borderColor = "transparent";
    ov.style.color = "transparent";
  });

  // Синхронизация скролла textarea -> overlay
  const handleScroll = useCallback(
    (e) => {
      const ov = overlayRef.current;
      if (ov) {
        ov.scrollTop = e.currentTarget.scrollTop;
        ov.scrollLeft = e.currentTarget.scrollLeft;
      }
      onScroll?.(e);
    },
    [onScroll],
  );

  // При изменении высоты textarea (auto-resize в ChatPage) подгоняем оверлей.
  useEffect(() => {
    const ta = taRef.current;
    const ov = overlayRef.current;
    if (!ta || !ov || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      ov.style.height = `${ta.offsetHeight}px`;
      ov.style.width = `${ta.offsetWidth}px`;
      ov.scrollTop = ta.scrollTop;
    });
    ro.observe(ta);
    return () => ro.disconnect();
  }, []);

  const overlayHtml = useMemo(() => buildOverlayHtml(value || "", issues), [value, issues]);

  // Открытие меню по координатам клика/тапа.
  const openMenuAt = useCallback(
    (clientX, clientY) => {
      const ta = taRef.current;
      if (!ta) return false;
      // Сначала пробуем выяснить смещение каретки из координат.
      let offset = getCaretOffsetFromPoint(ta, clientX, clientY);
      // Fallback: место каретки в textarea (после browser-обработки правого клика).
      if (offset == null) offset = ta.selectionStart ?? 0;

      const issue = findIssueAtOffset(issues, offset);
      if (!issue || !issue.suggestions.length) {
        setMenu(null);
        return false;
      }

      const { x, y, maxHeight } = placeMenu(clientX, clientY, issue.suggestions.length);
      setMenu({ x, y, maxHeight, issue });
      return true;
    },
    [issues, placeMenu],
  );

  const handleContextMenu = useCallback(
    (e) => {
      // Браузер уже успел поставить каретку в место клика к моменту contextmenu.
      const opened = openMenuAt(e.clientX, e.clientY);
      if (opened) e.preventDefault();
    },
    [openMenuAt],
  );

  // Long-press для мобильных
  const longPressRef = useRef({ timer: null, x: 0, y: 0 });
  const cancelLongPress = useCallback(() => {
    if (longPressRef.current.timer) {
      clearTimeout(longPressRef.current.timer);
      longPressRef.current.timer = null;
    }
  }, []);

  const handleTouchStart = useCallback(
    (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      longPressRef.current.x = touch.clientX;
      longPressRef.current.y = touch.clientY;
      cancelLongPress();
      longPressRef.current.timer = window.setTimeout(() => {
        openMenuAt(longPressRef.current.x, longPressRef.current.y);
      }, LONG_PRESS_MS);
    },
    [openMenuAt, cancelLongPress],
  );

  const handleTouchMove = useCallback(
    (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      const dx = touch.clientX - longPressRef.current.x;
      const dy = touch.clientY - longPressRef.current.y;
      if (Math.hypot(dx, dy) > LONG_PRESS_TOLERANCE_PX) cancelLongPress();
    },
    [cancelLongPress],
  );

  // Закрытие меню при клике вне / прокрутке / Esc.
  useEffect(() => {
    if (!menu) return;
    const onDocDown = (e) => {
      if (!e.target.closest?.(".spellMenu")) closeMenu();
    };
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("touchstart", onDocDown, { passive: true });
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("touchstart", onDocDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [menu, closeMenu]);

  const applySuggestion = useCallback(
    (suggestion) => {
      if (!menu?.issue) return;
      const ta = taRef.current;
      if (!ta) return;
      const { pos, len } = menu.issue;
      const before = (value || "").slice(0, pos);
      const after = (value || "").slice(pos + len);
      const next = `${before}${suggestion}${after}`;

      // Эмулируем native input event, чтобы существующий onChange отработал
      // штатно — заодно сработают авто-ресайз и debounce-проверка.
      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        "value",
      )?.set;
      if (setter) {
        setter.call(ta, next);
        ta.dispatchEvent(new Event("input", { bubbles: true }));
      } else if (onChange) {
        onChange({ target: { value: next }, currentTarget: ta });
      }

      // Возвращаем фокус и ставим каретку после заменённого слова.
      requestAnimationFrame(() => {
        const caret = pos + suggestion.length;
        ta.focus();
        try { ta.setSelectionRange(caret, caret); } catch { /* noop */ }
      });
      closeMenu();
    },
    [menu, value, onChange, closeMenu],
  );

  return (
    <div ref={wrapRef} className="spellWrap" style={style}>
      <div
        ref={overlayRef}
        className={`spellOverlay ${overlayClassName}`}
        aria-hidden="true"
      >
        <span dangerouslySetInnerHTML={{ __html: overlayHtml }} />
      </div>
      <textarea
        {...rest}
        ref={taRef}
        className={className}
        value={value}
        onChange={onChange}
        onScroll={handleScroll}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={cancelLongPress}
        onTouchCancel={cancelLongPress}
        spellCheck={false}
      />
      {menu
        ? createPortal(
            <div
              className="spellMenu spellMenuPortal"
              role="menu"
              style={{ left: menu.x, top: menu.y, maxHeight: menu.maxHeight }}
            >
              <div className="spellMenuTitle">{menu.issue.word}</div>
              <div className="spellMenuList">
                {menu.issue.suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="menuitem"
                    className="spellMenuItem"
                    onClick={() => applySuggestion(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
});
